import { Center, Loader } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { nprogress } from '@mantine/nprogress'
import { spotlight } from '@mantine/spotlight'
import { IconPlus } from '@tabler/icons-react'
import { MutationCache, QueryClient, QueryClientProvider, useIsFetching } from '@tanstack/react-query'
import { type AppId, appBase, appOfPath, apps, businessApps, crewApps, everyoneApps } from '@tnn/core'
import { type CreateI18nOptions, createI18n } from '@tnn/i18n'
import {
  type AsidePanel,
  type IconComponent,
  LinkProvider,
  type NavEntry,
  type NavSection,
  Shell,
  type ShellFab,
  UiProvider,
} from '@tnn/ui'
import type { i18n as I18n, TFunction } from 'i18next'
import {
  type ComponentPropsWithRef,
  type ComponentType,
  createContext,
  type ReactNode,
  StrictMode,
  Suspense,
  use,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react'
import { createRoot } from 'react-dom/client'
import { I18nextProvider, useTranslation } from 'react-i18next'
import {
  createBrowserRouter,
  Link,
  Outlet,
  type RouteObject,
  ScrollRestoration,
  useLocation,
  useMatches,
  useRoutes,
  useSearchParams,
} from 'react-router'
import { RouterProvider } from 'react-router/dom'
import { isTransient } from '../api/client'
import { AppScope, type Me, SessionGate, useSession } from '../auth/session'
import { PersonAvatar } from '../photo/photos'
import { ColorSchemeSync, PreferencesProvider, storedPreferences, usePreferences } from '../prefs/preferences'
import { AppUpdate, listenToWorker } from '../pwa/app'
import { usePushSync } from '../pwa/push'
import { type AppReport, reportRoutes } from '../report/reports'
import { pagePath, type ResourcePage } from '../resource/config'
import { ResourceFormPage } from '../resource/ResourceForm'
import { ResourceListPage } from '../resource/ResourceList'
import { useLiveEvents } from '../screen/screen'
import { AccountMenu, AppMark, AppSwitcher, appIcons, appImage, CommandPalette, DemoBadge, useAccessibleApps } from './chrome'
import { followLinks, setOsNavigate } from './go'
import { LoginPage } from './LoginPage'
import { loginUrl, scopedPath } from './links'
import { Navigate, useScopeApp } from './scope'
import { fromMenu, StackOutlet } from './stack'
import { LeaveFor, NotFound, RouteError } from './states'
import { useWaiting, useWaitingByApp, waitingKey, waitingOnMenu } from './waiting'

export interface NavContext {
  t: TFunction
  can: (action: string, subject: string) => boolean
  me: Me
  /** The app showing the menu: shared modules leave out work for others in a personal one (`AppInfo.personal`) */
  appId: AppId
}

/** One thing the create button makes: a new record's page, or a page opening on something (`/?scan=1`) */
export interface CreateEntry {
  key: string
  label: string
  icon: IconComponent
  href: string
  /** Shown only to those who may create it; none: to everyone */
  resource?: string
}

export interface AppDefinition {
  id: AppId
  /** Resources with generated list and form pages (also searchable with ⌘K) */
  resources?: ResourcePage[]
  /** Hand-written pages: dashboards, custom flows. Thananan OS's are หน้าแรก's, under `/h` (`apps.portal.path`) */
  routes?: RouteObject[]
  /**
   * Thananan OS's pages at the root, outside หน้าแรก's (`rootPages` in @tnn/core): หน้าแรก itself, แชท, รายชื่อ, the
   * company's apps. In the shell like every page, without หน้าแรก's menu beside them (but หน้าแรก)
   */
  rootRoutes?: RouteObject[]
  /**
   * Its reports page (`/reports`), one row each in order: a report made from its definition (`defineReport`) gets its
   * page at `/reports/<name>`; a hand-written one is among `routes`. The menu links it (`reportsFor` says if any is one's)
   */
  reports?: AppReport[]
  /** Pages for people not signed in, outside the shell (like sign-in): joining by invite, asking for an account */
  publicRoutes?: RouteObject[]
  nav: (ctx: NavContext) => NavSection[]
  /**
   * What the big create button makes (top of the menu on desktop, floating on phones), in order. One the person may
   * make is the button itself; several are a menu under "Create"
   */
  create?: (ctx: NavContext) => CreateEntry[]
  /** Panels on the right rail (desktop) or in a sheet (tablet/phone) */
  asidePanels?: (ctx: NavContext) => AsidePanel[]
  /** Under the app's menu (Drive's storage used): a component, so it can read queries */
  sideFooter?: ComponentType
  /**
   * Live numbers on menu items and rail panels, keyed by their `key` (e.g. `{ late: 3, free: 2 }`), in place of what
   * waits for the person there (`/api/me/approvals`, put on the menu by itself: `waitingOnMenu`).
   * A hook, so it can read queries; it runs on every page of the app.
   */
  useCounts?: () => Record<string, number | undefined>
  /** Open to every signed-in user, like the portal */
  open?: boolean
  /**
   * Thananan OS's own frame for every app (`Shell` prop `osShell`): a top bar of the bottom bar's items and the apps on a
   * computer, the app's name at the top and its menu as a sheet on a phone; a route with `handle: { flush: true }` fills
   * the area edge to edge
   */
  rail?: boolean
  i18n?: CreateI18nOptions['extra']
}

const AppNavContext = createContext<NavSection[]>([])
/** Thananan OS's menu as the shell shows it (counts, the item in front): หน้าแรก on a phone draws its tiles from it */
export const useAppNav = () => use(AppNavContext)

/**
 * React Router link that accepts `href`, so UI components stay router-free: a path from the app's own root goes under the
 * app's path (`/waybills` in TMS → `/tms/waybills`), a full address on this site becomes its path. A menu's link
 * (`data-menu`) says so
 */
function RouterLink({ href = '', ...props }: ComponentPropsWithRef<'a'>) {
  const app = useScopeApp()
  const here = href.startsWith(window.location.origin) ? href.slice(window.location.origin.length) || '/' : href
  return <Link to={scopedPath(app, here)} state={'data-menu' in props ? fromMenu : undefined} {...props} />
}

function RootRoute() {
  return (
    <LinkProvider value={RouterLink}>
      <ScrollRestoration />
      <Outlet />
    </LinkProvider>
  )
}

/** Thin top progress bar while anything is loading. */
function FetchProgress() {
  const fetching = useIsFetching() > 0
  useEffect(() => {
    if (!fetching) return nprogress.complete()
    const timer = setTimeout(() => nprogress.start(), 250)
    return () => clearTimeout(timer)
  }, [fetching])
  return null
}

const noCounts = (): Record<string, number | undefined> => ({})

/** What a list link starts with (its filters, sort, search): the list is still its own without them */
const listParam = (key: string) => key.startsWith('f.') || key === 'sort' || key === 'q' || key === 'page'

/**
 * Picks the single most specific nav item matching the URL; and a bottom item whose pages it is among (`owns`, หน้าแรก's
 * under `/h`), the one in front on the top or bottom bar
 */
function markActive(sections: NavSection[], pathname: string, search: string): NavSection[] {
  const have = new URLSearchParams(search)
  const under = (path: string) => (path === '/' ? pathname === '/' : pathname === path || pathname.startsWith(`${path}/`))
  const score = (href: string, owns: string[] = []) => {
    const [path = '/', query] = href.split('?')
    if (!under(path)) return owns.some(under) ? 1 : -1
    // A link that picks a mode (`/cash-transfers/new?mode=receive`, `?scan=1`) is that mode only, not its whole path
    const params = [...new URLSearchParams(query ?? '')]
    if (params.some(([k, v]) => !listParam(k) && have.get(k) !== v)) return -1
    // A link owns its path (list, record pages); one whose filters also match the URL wins
    const exact = !!query && params.every(([k, v]) => have.get(k) === v)
    return path.length + (exact ? 1000 : 0)
  }
  let best: string | undefined
  let bestScore = -1
  for (const s of sections)
    for (const item of s.items) {
      if (item.external) continue
      const sc = score(item.href, item.owns)
      if (sc > bestScore) {
        bestScore = sc
        best = item.key
      }
    }
  const owner = sections.flatMap((s) => s.items).find((i) => i.bottom && i.owns?.some(under))?.key
  return sections.map((s) => ({ ...s, items: s.items.map((i) => ({ ...i, active: i.key === best || i.key === owner })) }))
}

/** A link opens this page: its path, and its query too if it has one (`/?scan=1` is the scanner open, `/` is not) */
function opens(href: string, pathname: string, search: string) {
  const [path, query] = href.split('?')
  const have = new URLSearchParams(search)
  return pathname === path && [...new URLSearchParams(query ?? '')].every(([k, v]) => have.get(k) === v)
}

const pageLoading = (
  <Center flex={1}>
    <Loader type="dots" />
  </Center>
)

/** An app's menu as the shell shows it: under the app's path, the item in front, the numbers on each */
function sectionsOf(
  def: AppDefinition,
  ctx: NavContext,
  counts: Record<string, number | undefined>,
  approvals: Parameters<typeof waitingOnMenu>[0],
  location: { pathname: string; search: string },
): NavSection[] {
  const menu = def.nav(ctx)
  const waiting = waitingOnMenu(approvals, def.id, menu, def.resources ?? [], ctx.t)
  const placed = menu.map((s) => ({ ...s, items: s.items.map((i) => (i.external ? i : { ...i, href: scopedPath(def.id, i.href) })) }))
  return markActive(placed, location.pathname, location.search).map((s) => ({
    ...s,
    items: s.items.map((i) => {
      const parts = waiting[i.key]
      if (counts[i.key]) return { ...i, count: counts[i.key] }
      return parts ? { ...i, count: parts.reduce((n, p) => n + p.count, 0), parts } : i
    }),
  }))
}

/** An app's live menu numbers (`useCounts`, a hook of its own): mounted per app, so switching apps keeps hooks in order */
function Counts({
  of,
  onChange,
}: {
  of: () => Record<string, number | undefined>
  onChange: (counts: Record<string, number | undefined>) => void
}) {
  const counts = of()
  const key = JSON.stringify(counts)
  // biome-ignore lint/correctness/useExhaustiveDependencies: when the numbers change, not the object
  useEffect(() => onChange(counts), [key])
  return null
}

/** Where each app was last, so its tab goes back there; and the app last in front, where a phone's แอพ button goes */
const lastPath = new Map<AppId, string>()
let lastApp: AppId | undefined

/**
 * The shell of Thananan OS and every app in it (`osShell`). A computer: the top bar (หน้าแรก · แชท · รายชื่อ and the
 * apps), then the app in front's menu beside its pages (Thananan OS's own: หน้าแรก's menu beside หน้าแรก's pages, those
 * under `/h`; the page alone at the root, like แชท or the apps). A phone:
 * Thananan OS's bottom bar always, its แอพ back to the app last in front (pressed again there, to change app), ☰ at the
 * top for the app's menu. `routes`: every page, which a phone keeps in stacks, one per tab (`StackOutlet`)
 */
function AppLayout({ os, routes }: { os: AppDefinition; routes: RouteObject[] }) {
  const { t } = useTranslation()
  // The shell's own phone breakpoint: there pages stack like a native app's, on a computer they replace each other
  const phone = useMediaQuery('(max-width: 47.99em)', false, { getInitialValueInEffect: false })
  const { me, ability } = useSession()
  const { adopt } = usePreferences()
  const location = useLocation()
  const appId = appOfPath(location.pathname)
  const inApp = appId !== 'portal'
  const loaded = useLoadedApp(appId)
  const def = inApp ? loaded : os
  const openable = useAccessibleApps().filter((id) => id !== 'portal')
  usePushSync()
  useLiveEvents()

  // biome-ignore lint/correctness/useExhaustiveDependencies: adopt the server's preferences once per user
  useEffect(() => {
    if (me.preferences) adopt(me.preferences)
  }, [me.user.id])
  useEffect(() => {
    if (!inApp) return
    lastPath.set(appId, location.pathname + location.search)
    lastApp = appId
  }, [inApp, appId, location.pathname, location.search])

  const ctxOf = (id: AppId): NavContext => ({ t, can: (a, s) => ability.can(a, s), me, appId: id })
  const [osCounts, setOsCounts] = useState<Record<string, number | undefined>>({})
  const [appCounts, setAppCounts] = useState<Record<string, number | undefined>>({})
  const approvals = useWaiting().data?.items ?? []
  const waitingIn = useWaitingByApp()
  const osSections = sectionsOf(os, ctxOf('portal'), osCounts, approvals, location)
  const appSections = inApp && loaded ? sectionsOf(loaded, ctxOf(appId), appCounts, approvals, location) : undefined
  const sections = inApp ? (appSections ?? []) : osSections

  // The top bar (a phone's bottom bar): Thananan OS's own items, then the apps, the one in front marked, each going back
  // to where it was. On a computer an app's tab is the one in front on its pages; a phone's is its bar's แอพ
  const apps_: NavEntry[] = openable.map((id) => ({
    key: `app-${id}`,
    label: t(`apps.${id}`),
    icon: appIcons[apps[id].icon],
    // What waits there, on its icon
    mark: (size: number) => <AppMark id={id} size={size} count={waitingIn[id] ?? 0} />,
    href: lastPath.get(id) ?? appBase(id),
    active: id === appId,
  }))
  const osItems = inApp && !phone ? osSections.map((s) => ({ ...s, items: s.items.map((i) => ({ ...i, active: false })) })) : osSections
  const nav: NavSection[] = apps_.length ? [...osItems, { key: 'apps', label: t('apps.other'), items: apps_ }] : osItems

  const commands = sections.flatMap((s) => s.items).map((i) => ({ id: i.key, label: i.label, icon: i.icon, href: i.href }))
  // On a page it opens (a new run, the scanner open) a button for that one thing has nothing to do; a menu of several
  // stays beside the page but does not float on a phone, over the page's own buttons
  const makes = (def?.create?.(ctxOf(appId)) ?? [])
    .filter((e) => !e.resource || ability.can('create', e.resource))
    .map((e) => ({ ...e, href: scopedPath(appId, e.href) }))
  const onCreatePage = makes.some((e) => opens(e.href, location.pathname, location.search))
  // A route that is the whole screen on a phone (`handle: { bare: true }`, a chat room)
  const handles = useMatches().map((m) => (m.handle ?? {}) as { bare?: boolean; flush?: boolean })
  const bare = handles.some((h) => h.bare)
  // A page that fills its area edge to edge on a computer (`handle: { flush: true }`, chat)
  const flush = handles.some((h) => h.flush)
  // หน้าแรก and its pages (under `/h`, the bottom item that `owns` them), on a computer, have หน้าแรก's own menu beside
  // them, as an app's page has the app's: the menu's pages that are not on the bottom bar. The root's others are alone
  const front = osSections.flatMap((s) => s.items).find((i) => i.bottom && i.active)
  const mine = !phone && !inApp && !flush && !!front?.owns
  const ownMenu = osSections
    .map((s) => ({ ...s, items: s.items.filter((i) => !i.bottom && !i.external) }))
    .filter((s) => s.items.length > 0)
  const [only] = makes
  const fab: ShellFab | undefined =
    !only || (onCreatePage && makes.length === 1)
      ? undefined
      : makes.length === 1
        ? only
        : { label: t('actions.create'), icon: IconPlus, items: makes, floats: !onCreatePage }
  const panels = def?.asidePanels?.(ctxOf(appId)).map((p) => (appCounts[p.key] ? { ...p, badge: appCounts[p.key] } : p))
  // A phone's tabs, each keeping its pages: each bottom item's page, and each app with all of its own
  const osRoots = osSections.flatMap((s) => s.items).flatMap((i) => (i.external ? [] : [i.href]))
  const tabOf = (at: { pathname: string; search: string }) => {
    const id = appOfPath(at.pathname)
    return id !== 'portal' ? `app:${id}` : (osRoots.find((r) => r === at.pathname + at.search || r === at.pathname) ?? '')
  }

  return (
    <AppNavContext value={osSections}>
      <FetchProgress />
      <Counts key="portal" of={os.useCounts ?? noCounts} onChange={setOsCounts} />
      {inApp && loaded && <Counts key={appId} of={loaded.useCounts ?? noCounts} onChange={setAppCounts} />}
      <Shell
        appName={t('apps.portal')}
        appMark={<AppMark id="portal" size={36} />}
        nav={nav}
        fab={fab}
        side={
          inApp
            ? {
                title: t(`apps.${appId}`),
                mark: <AppMark id={appId} size={32} />,
                nav: appSections ?? [],
                fab,
                footer: loaded?.sideFooter && <loaded.sideFooter />,
              }
            : mine
              ? { title: t('shell.home'), mark: <PersonAvatar name={me.user.name} userId={me.user.id} size={32} />, nav: ownMenu, fab }
              : undefined
        }
        searchPlaceholder={t('shell.searchPlaceholder')}
        onSearch={spotlight.open}
        actions={<AppSwitcher />}
        account={<AccountMenu />}
        asidePanels={panels}
        badge={me.demo ? <DemoBadge /> : undefined}
        scrollKey={location.key}
        stacked={phone}
        bare={phone && bare}
        osShell={!!os.rail}
        flush={flush}
        lastApp={lastApp && `app-${lastApp}`}
        // Thananan OS's page of the company's apps, where one asks for another (`StorePage`), a page of its own
        askApps={{ label: t('shell.askApps'), href: '/apps', active: location.pathname === '/apps' }}
        labels={{
          menu: t('shell.menu'),
          expand: t('shell.expand'),
          collapse: t('shell.collapse'),
          close: t('actions.close'),
          switchApp: t('shell.switchApp'),
          apps: t('shell.apps'),
        }}
      >
        {phone ? (
          <StackOutlet app="portal" routes={routes} tabOf={tabOf} fallback={pageLoading} />
        ) : (
          <Suspense fallback={pageLoading}>
            <Outlet />
          </Suspense>
        )}
      </Shell>
      <AppScope appId={appId} gate={false}>
        <CommandPalette actions={commands} pages={def?.resources ?? []} />
      </AppScope>
    </AppNavContext>
  )
}

function Themed({ seed, children }: { seed: string; children: ReactNode }) {
  const { prefs } = usePreferences()
  const { t } = useTranslation()
  return (
    <UiProvider seed={seed} locale={prefs.locale} scale={prefs.fontScale} closeLabel={t('actions.close')}>
      <ColorSchemeSync />
      {children}
    </UiProvider>
  )
}

/** The one sign-in page, Thananan OS's `/login` */
function LoginRoute() {
  const { t } = useTranslation()
  return <LoginPage appName={t('apps.portal')} />
}

/**
 * An app's own sign-in address from before (`/tms/login`, bookmarks): the one sign-in page now, coming back to where
 * it was going (`callbackUrl`, or the older in-app `redirect`) or to the app's home
 */
function ToLogin({ base }: { base: string }) {
  const [params] = useSearchParams()
  const redirect = params.get('redirect')
  const back = params.get('callbackUrl') ?? (redirect ? base + redirect : `${base}/`)
  return <LeaveFor href={loginUrl(back)} />
}

type AppLoaders = Record<AppId, () => Promise<AppDefinition>>

/** Every app's code, loaded the first time one of its pages opens (and its texts added to the one i18n) */
let loaders: Partial<AppLoaders> = {}
let texts: I18n | null = null
const loadedApps = new Map<AppId, AppDefinition>()
const loadingApps = new Map<AppId, Promise<AppDefinition>>()
const appListeners = new Set<() => void>()

function loadApp(id: AppId): Promise<AppDefinition> {
  const known = loadingApps.get(id)
  if (known) return known
  const load = loaders[id]
  if (!load) return Promise.reject(new Error(`No app ${id}`))
  const loading = load().then((def) => {
    for (const [lng, namespaces] of Object.entries(def.i18n ?? {}))
      for (const [ns, resources] of Object.entries(namespaces ?? {})) texts?.addResourceBundle(lng, ns, resources, true, true)
    loadedApps.set(id, def)
    for (const listener of appListeners) listener()
    return def
  })
  loadingApps.set(id, loading)
  return loading
}

const subscribeApps = (listener: () => void) => {
  appListeners.add(listener)
  return () => appListeners.delete(listener)
}
const useLoadedApp = (id: AppId) => useSyncExternalStore(subscribeApps, () => loadedApps.get(id))

/** An app's pages, from its own root: hand-written, reports, its records' lists and forms, its old sign-in address */
function routesOf(def: AppDefinition): RouteObject[] {
  const base = appBase(def.id)
  const resourceRoutes: RouteObject[] = (def.resources ?? []).flatMap((page) => {
    const path = pagePath(page.resource)
    return [
      { path, element: page.listElement ?? <ResourceListPage page={page} /> },
      { path: `${path}/new`, element: page.formElement ?? <ResourceFormPage page={page} /> },
      { path: `${path}/:id`, element: page.formElement ?? <ResourceFormPage page={page} /> },
    ]
  })
  return [
    ...(def.routes ?? []),
    ...(def.reports ? reportRoutes(def.reports) : []),
    ...resourceRoutes,
    ...(def.id !== 'portal' ? [{ path: 'login', element: <ToLogin base={base} /> }] : []),
    { path: '*', element: <NotFound /> },
  ]
}

/** An app at its path (`/tms/*`): its code loaded, its pages routed from its own root, as that app (`AppScope`) */
function AppHost({ id }: { id: AppId }) {
  const def = use(loadApp(id))
  const routes = useMemo(() => routesOf(def), [def])
  const element = useRoutes(routes)
  return (
    <AppScope appId={id} open={def.open}>
      {element}
    </AppScope>
  )
}

/**
 * Starts Thananan OS with every app in it (Thananan OS's `main.tsx` passes how to load each): one router, each app under
 * its path (`apps[id].path`) and its code loaded the first time one of its pages opens; moving between apps loads no page
 */
export async function startApps(load: AppLoaders) {
  loaders = load
  texts = createI18n({ lng: storedPreferences().locale })
  const os = await loadApp('portal')
  const first = appOfPath(window.location.pathname)
  if (first !== 'portal') await loadApp(first).catch(() => undefined)
  start(os, texts)
}

/** Providers, the one router, sign-in, permissions and the shell, for Thananan OS (`os`) and every app in it */
function start(os: AppDefinition, i18n: I18n) {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: { staleTime: 15_000, retry: (count, error) => isTransient(error) && count < 2 },
    },
    // Anything saved may be what waited (approved, paid): the menu's numbers are asked again
    mutationCache: new MutationCache({ onSuccess: () => queryClient.invalidateQueries({ queryKey: waitingKey }) }),
  })

  // Thananan OS's own pages at the root (หน้าแรก, แชท, รายชื่อ, the apps), หน้าแรก's under `/h` (`/h` alone goes to `/`),
  // each app's under its path; nothing found is Thananan OS's not-found page
  const pages: RouteObject[] = [
    ...(os.rootRoutes ?? []),
    { path: apps.portal.path, children: [{ index: true, element: <Navigate to="/" replace /> }, ...routesOf(os)] },
    ...[...businessApps, ...crewApps, ...everyoneApps].map((id) => ({ path: `${apps[id].path}/*`, element: <AppHost id={id} /> })),
    { path: '*', element: <NotFound /> },
  ]
  const tree: RouteObject[] = [
    {
      element: <RootRoute />,
      errorElement: <RouteError />,
      children: [
        { path: 'login', element: <LoginRoute /> },
        ...(os.publicRoutes ?? []),
        {
          element: (
            <SessionGate appId="portal" open>
              <AppLayout os={os} routes={pages} />
            </SessionGate>
          ),
          children: pages,
        },
      ],
    },
  ]
  const router = createBrowserRouter(tree)
  // Another app's page asked for by script (search, a report): a move in this router, no page load
  setOsNavigate((path) => router.navigate(path))
  followLinks((path) => router.navigate(path))

  // A notification arrived: refresh what is on screen. One was tapped: go there
  listenToWorker({
    push: () => queryClient.invalidateQueries(),
    open: (path) => router.navigate(path),
  })

  // The browser tab: Thananan OS until a page names itself, and its icon
  document.title = apps.portal.name
  const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
  const icon = appImage('portal')
  if (favicon && icon) favicon.href = icon

  const root = document.getElementById('root')
  if (!root) throw new Error('Missing #root element')
  createRoot(root).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <I18nextProvider i18n={i18n}>
          <PreferencesProvider>
            <Themed seed={apps.portal.seed}>
              <AppUpdate router={router} />
              <RouterProvider router={router} />
            </Themed>
          </PreferencesProvider>
        </I18nextProvider>
      </QueryClientProvider>
    </StrictMode>,
  )
}
